import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteLayout } from "@/components/SiteLayout";
import { PageHero } from "@/components/PageHero";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { breadcrumbScriptTag } from "@/lib/breadcrumbSchema";

const SITE_URL = "https://coloradowebimpressions.com";
const LAST_UPDATED = "April 30, 2026";
const LAST_REVIEWED = "April 30, 2026";

export const Route = createFileRoute("/accessibility")({
  headers: () => ({
    "Cache-Control": "public, max-age=0, s-maxage=604800, stale-while-revalidate=2592000",
    "CDN-Cache-Control": "public, s-maxage=604800, stale-while-revalidate=2592000",
  }),
  head: () => ({
    meta: [
      { title: "Accessibility Statement | Colorado Web Impressions" },
      {
        name: "description",
        content:
          "Our commitment to accessible web design. Read about WCAG 2.1 AA conformance, the steps we take, and how to report an accessibility issue.",
      },
      { name: "robots", content: "index, follow" },
      { property: "og:title", content: "Accessibility Statement | Colorado Web Impressions" },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/accessibility` }],
    scripts: [
      breadcrumbScriptTag([
        { name: "Home", path: "/" },
        { name: "Accessibility" },
      ]),
    ],
  }),
  component: AccessibilityPage,
});

function AccessibilityPage() {
  return (
    <SiteLayout>
      <PageHero
        eyebrow="Website Accessibility Statement"
        title="Accessibility Statement"
        description={`Last updated ${LAST_UPDATED} · Last reviewed ${LAST_REVIEWED}. We build websites everyone can use — and we hold our own site to the same standard.`}
      >
        <div className="flex justify-center">
          <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Accessibility" }]} />
        </div>
      </PageHero>

      <section className="bg-background py-16 lg:py-20">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <article className="prose-article">
            <h2>Our commitment</h2>
            <p>
              Heidlebaugh Digital LLC (dba Colorado Web Impressions) is
              committed to making coloradowebimpressions.com accessible to
              people of all abilities, including people who use screen readers,
              keyboard-only navigation, voice control, screen magnification,
              and other assistive technology. Accessibility is treated as a
              core requirement of every page we ship, not an afterthought.
            </p>

            <h2>Standards we follow</h2>
            <p>
              We design and build to the{" "}
              <strong>
                Web Content Accessibility Guidelines (WCAG) 2.1, Level AA
              </strong>{" "}
              published by the W3C. We also align with the relevant
              accessibility provisions of:
            </p>
            <ul>
              <li>The Americans with Disabilities Act (ADA), Title III</li>
              <li>Section 508 of the U.S. Rehabilitation Act</li>
              <li>
                Colorado HB 21-1110 (Colorado Laws for Persons with
                Disabilities) technology accessibility standards
              </li>
            </ul>

            <h2>Conformance status</h2>
            <p>
              This website is designed to{" "}
              <strong>partially conform</strong> with WCAG 2.1 Level AA.
              &quot;Partially conforms&quot; means that some parts of the
              content do not fully conform to the accessibility standard —
              this is the honest, audit-correct phrasing. No website that
              contains third-party embeds, user-generated content, or live
              media can truthfully claim full conformance, and we won&apos;t
              pretend otherwise. We continuously work to close the gap.
            </p>

            <h2>What we do</h2>
            <ul>
              <li>Semantic HTML and a logical heading hierarchy on every page.</li>
              <li>Descriptive alt text on every meaningful image.</li>
              <li>
                Full keyboard navigation with visible focus indicators that
                meet WCAG 2.1 AA contrast.
              </li>
              <li>
                Color contrast meeting or exceeding 4.5:1 for body text and
                3:1 for large text and UI components, in both light and dark
                modes.
              </li>
              <li>
                Responsive layouts that remain usable at 200% zoom and reflow
                to a 320 CSS pixel viewport without horizontal scrolling.
              </li>
              <li>
                Forms with programmatically associated labels, clear error
                messaging, and required-field indication beyond color alone.
              </li>
              <li>
                Skip-to-content links, ARIA landmarks, and accessible names on
                all interactive elements.
              </li>
              <li>
                Motion-reducing preferences respected via{" "}
                <code>prefers-reduced-motion</code>.
              </li>
            </ul>

            <h2>No accessibility overlay or widget</h2>
            <p>
              We deliberately do <strong>not</strong> use accessibility
              overlays, toolbars, or widgets (such as AccessiBe, UserWay,
              EqualWeb, AudioEye, or similar). Disability-rights organizations
              and accessibility professionals broadly recommend against these
              tools because they often interfere with users&apos; own
              assistive technology and don&apos;t fix the underlying
              accessibility issues. Instead, we build accessibility directly
              into the site&apos;s code so it works correctly with whatever
              assistive technology you already use.
            </p>

            <h2>Known limitations</h2>
            <p>
              No website is perfect. Despite our efforts, you may encounter
              accessibility barriers — particularly within third-party embeds
              (maps, video players, scheduling tools, review widgets), older
              archived blog content, or PDFs we have not yet remediated. If
              you find one, we treat it as a bug and fix it on a clear
              timeline (see below).
            </p>

            <h2>Report an accessibility issue</h2>
            <p>
              We welcome your feedback. If you have trouble accessing any
              part of this website, please contact us with the following
              information so we can reproduce and fix the problem quickly:
            </p>
            <ul>
              <li>The URL of the page where the issue occurs</li>
              <li>A short description of the problem</li>
              <li>The browser, device, and assistive technology you are using (if applicable)</li>
              <li>Your preferred method of follow-up (email or phone)</li>
            </ul>
            <p>
              <strong>Email:</strong>{" "}
              <a href="mailto:info@coloradowebimpressions.com?subject=Accessibility%20Issue" aria-label="Email Colorado Web Impressions">
                info@coloradowebimpressions.com
              </a>
              <br />
              <strong>Phone:</strong>{" "}
              <a href="tel:+17194193935" aria-label="Call (719) 419-3935">(719) 419-3935</a>
              <br />
              <strong>Mail:</strong> Heidlebaugh Digital LLC dba Colorado Web
              Impressions, 3210 E Woodmen Rd Ste 110, Colorado Springs, CO 80920
            </p>
            <p>
              <strong>Our response commitment:</strong> we acknowledge every
              accessibility report within <strong>2 business days</strong>,
              respond with a substantive reply within{" "}
              <strong>5 business days</strong>, and aim to resolve confirmed
              issues within <strong>30 days</strong>. If a fix will take
              longer, we will tell you why and give you a target date.
            </p>

            <h2>Third-party content</h2>
            <p>
              Some embedded content (such as third-party maps, video players,
              or scheduling widgets) may not fully conform to WCAG 2.1 AA. We
              choose vendors with strong accessibility track records and avoid
              embeds that have known major issues.
            </p>

            <h2>Browser and assistive technology compatibility</h2>
            <p>
              We test our website against current versions of Chrome, Safari,
              Firefox, and Edge on both desktop and mobile. For assistive
              technology, we verify core flows with VoiceOver on macOS and iOS,
              NVDA on Windows, and TalkBack on Android. Older browsers may
              still load the site, but visual polish and animation behavior
              are not guaranteed below the latest two major versions.
            </p>

            <h2>How we test and maintain accessibility</h2>
            <p>
              Accessibility is part of our build process, not an afterthought.
              Every new page ships through automated checks (axe-core, Lighthouse)
              plus a manual keyboard-only pass before it goes live. When we
              redesign a section, we re-run contrast checks against the latest
              WCAG 2.1 AA color thresholds and validate that focus order still
              follows visual reading order.
            </p>
            <p>
              If you are evaluating us for a project that has its own
              accessibility requirements, we are happy to share the exact
              pre-launch checklist we verify before any site goes live —
              just <Link to="/contact">get in touch</Link>.
            </p>

            <h2>Formal complaints</h2>
            <p>
              If you have reported an accessibility barrier and feel it has
              not been resolved adequately, you may escalate the matter by
              writing to the same email address above with the subject line
              &quot;Accessibility Complaint.&quot; A senior team member will
              personally review the report and respond within 10 business days
              with either a fix, a documented workaround, or a target
              resolution date.
            </p>
          </article>
        </div>
      </section>
    </SiteLayout>
  );
}
